import clsx from "clsx";
import React, { useEffect, useRef, useState, forwardRef } from "react";
import { createPortal } from "react-dom";

interface InputWithSuggestionBoxProps {
  suggestions: string[];
  groupedSuggestions?: Array<{
    title: string;
    options: string[];
  }>;
  placeholder?: string;
  onInputChange?: (value: string) => void;
  defaultInputValue?: string;
  formErrors?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  dropdownTextClassName?: string;
  dropdownZIndex?: number;
  showAllSuggestionsOnFocus?: boolean;
  inputName?: string;
  mandatoryEmpty?: boolean;
  selectOnly?: boolean;
  showDropdownIcon?: boolean;
  forceSelection?: boolean;
}

const InputWithSuggestionBox = forwardRef<HTMLInputElement, InputWithSuggestionBoxProps>(
  (
    {
      suggestions,
      groupedSuggestions = [],
      placeholder,
      onInputChange,
      defaultInputValue = "",
      formErrors = false,
      onKeyDown,
      dropdownTextClassName,
      dropdownZIndex = 9999,
      showAllSuggestionsOnFocus = false,
      inputName,
      mandatoryEmpty = false,
      selectOnly = false,
      showDropdownIcon = false,
      forceSelection = false,
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(defaultInputValue);
    const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
    const [filteredSuggestionGroups, setFilteredSuggestionGroups] = useState<
      Array<{
        title: string;
        options: string[];
      }>
    >([]);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(-1);
    const suggestionBoxRef = useRef<HTMLDivElement>(null);
    const inputContainerRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

    const allSelectableOptions = groupedSuggestions.length > 0
      ? groupedSuggestions.flatMap((group) => group.options || [])
      : suggestions;

    const getMatchedOption = (value: string) => {
      const normalizedValue = value.trim().toLowerCase();
      if (!normalizedValue) return null;
      return allSelectableOptions.find((option) => option.trim().toLowerCase() === normalizedValue) || null;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (selectOnly) {
        return;
      }
      const value = e.target.value;
      setInputValue(value);
      updateFilteredSuggestions(value);
      setActiveSuggestionIndex(-1);
      if (onInputChange) {
        onInputChange(value);
      }
    };

    const updateFilteredSuggestions = (value: string) => {
      if (groupedSuggestions.length > 0) {
        const groupedOptionSet = new Set(groupedSuggestions.flatMap((group) => group.options));
        const filteredGroups = groupedSuggestions
          .map((group) => ({
            title: group.title,
            options: (group.options || []).filter((option) => option.toLowerCase().includes(value.toLowerCase())),
          }))
          .filter((group) => group.options.length > 0);

        if (!selectOnly && !forceSelection && !groupedOptionSet.has(value) && value) {
          filteredGroups.push({
            title: "",
            options: [value],
          });
        }

        setFilteredSuggestionGroups(filteredGroups);
        setFilteredSuggestions([]);
        return;
      }

      const newFilteredSuggestions = suggestions.filter((suggestion) =>
        suggestion.toLowerCase().includes(value.toLowerCase()),
      );
      if (!selectOnly && !forceSelection && !newFilteredSuggestions.includes(value) && value) {
        newFilteredSuggestions.push(value);
      }
      setFilteredSuggestions(newFilteredSuggestions);
      setFilteredSuggestionGroups([]);
    };

    const handleSuggestionClick = (suggestion: string) => {
      setInputValue(suggestion);
      setFilteredSuggestions([]);
      setFilteredSuggestionGroups([]);
      setActiveSuggestionIndex(-1);
      if (onInputChange) {
        onInputChange(suggestion);
      }
    };

    const showAllSuggestions = () => {
      if (groupedSuggestions.length > 0) {
        const allGroups = groupedSuggestions
          .map((group) => ({
            title: group.title,
            options: group.options || [],
          }))
          .filter((group) => group.options.length > 0);

        setFilteredSuggestionGroups(allGroups);
        setFilteredSuggestions([]);
        setActiveSuggestionIndex(-1);
        return;
      }

      setFilteredSuggestions(suggestions || []);
      setFilteredSuggestionGroups([]);
      setActiveSuggestionIndex(-1);
    };

    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const clickedInsideInput = suggestionBoxRef.current?.contains(targetNode);
      const clickedInsideDropdown = dropdownRef.current?.contains(targetNode);
      if (!clickedInsideInput && !clickedInsideDropdown) {
        setFilteredSuggestions([]);
        setFilteredSuggestionGroups([]);
      }
    };

    const handleInputFocus = () => {
      if (showAllSuggestionsOnFocus) {
        showAllSuggestions();
        return;
      }
      updateFilteredSuggestions(inputValue);
    };

    const handleInputClick = () => {
      if (showAllSuggestionsOnFocus) {
        showAllSuggestions();
      }
    };

    const handleInputBlur = () => {
      if (!forceSelection || selectOnly) {
        return;
      }

      const trimmedValue = inputValue.trim();
      if (!trimmedValue) {
        setInputValue("");
        if (onInputChange) {
          onInputChange("");
        }
        return;
      }

      const matchedOption = getMatchedOption(trimmedValue);
      if (matchedOption) {
        setInputValue(matchedOption);
        if (onInputChange) {
          onInputChange(matchedOption);
        }
        return;
      }

      setInputValue("");
      if (onInputChange) {
        onInputChange("");
      }
    };

    const updateDropdownPosition = () => {
      const rect = inputContainerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    useEffect(() => {
      setInputValue(defaultInputValue || "");
    }, [defaultInputValue]);

    useEffect(() => {
      const hasSuggestions = filteredSuggestions.length > 0 || filteredSuggestionGroups.length > 0;
      if (!hasSuggestions) return;
      updateDropdownPosition();
      const handleReposition = () => updateDropdownPosition();
      window.addEventListener("resize", handleReposition);
      window.addEventListener("scroll", handleReposition, true);
      return () => {
        window.removeEventListener("resize", handleReposition);
        window.removeEventListener("scroll", handleReposition, true);
      };
    }, [filteredSuggestions.length, filteredSuggestionGroups.length]);

    const handleKeyDownInternal = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    return (
      <div className="relative w-full" ref={suggestionBoxRef}>
        <input
          name={inputName}
          data-medicine-required-empty={mandatoryEmpty ? "true" : undefined}
          ref={(node) => {
            inputContainerRef.current = node;
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          value={inputValue}
          readOnly={selectOnly}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          onKeyDown={handleKeyDownInternal}
          className={clsx(
            "w-full h-8 p-4 bg-transparent border-gray-300 rounded-md text-xs",
            showDropdownIcon && "pr-8",
            formErrors && "border-red-500",
          )}
        />
        {showDropdownIcon && (
          <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-[#6B7280]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        )}
        {(filteredSuggestions.length > 0 || filteredSuggestionGroups.length > 0) &&
          createPortal(
            <ul
              ref={dropdownRef}
              className={clsx(
                "fixed max-h-40 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-md",
                dropdownTextClassName || "text-sm",
              )}
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                zIndex: dropdownZIndex,
              }}
            >
              {filteredSuggestionGroups.length > 0
                ? filteredSuggestionGroups.map((group, groupIndex) => (
                  <React.Fragment key={`${group.title}-${groupIndex}`}>
                    {group.title ? (
                      <li className="px-3 pt-2 pb-1 text-[10px] font-semibold tracking-[0.16em] text-[#98A2B3] uppercase select-none">
                        <div>{group.title}</div>
                        <div className="mt-1 border-t border-[#D9D9D9]" />
                      </li>
                    ) : null}
                    {group.options.map((suggestion, optionIndex) => (
                      <li
                        key={`${group.title}-${suggestion}-${optionIndex}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSuggestionClick(suggestion);
                        }}
                        className="px-3 py-1.5 text-xs leading-5 cursor-pointer hover:bg-[#F3F4F6] text-[#1F1F23]"
                      >
                        {suggestion}
                      </li>
                    ))}
                  </React.Fragment>
                ))
                : filteredSuggestions.map((suggestion, index) => (
                  <li
                    key={index}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSuggestionClick(suggestion);
                    }}
                    className={`p-2 cursor-pointer hover:text-black ${
                      index === activeSuggestionIndex ? "bg-gray-800 text-white" : ""
                    }`}
                  >
                    {suggestion}
                  </li>
                ))}
            </ul>,
            document.body,
          )}
      </div>
    );
  },
);

export default InputWithSuggestionBox;
